import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { ExternalLink, Lock, Info } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ChartWidget } from '../components/workspace/ChartWidget';
import { MapWidget } from '../components/workspace/MapWidget';
import { TextWidget } from '../components/workspace/TextWidget';
import { TableWidget } from '../components/workspace/TableWidget';
import { useWorkspaces, MANAGED_TEMPLATES, type Widget } from '../hooks/useWorkspaces';
import { useUserProfile } from '../hooks/useUserProfile';
import type { LocationOverride } from '../components/workspace/LocationSelector';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
const ResponsiveGridLayout = WidthProvider(Responsive);
// Map user profile region to a LocationOverride
const REGION_OVERRIDES: Record<string, LocationOverride> = {
  'central-europe': {
    regionId: 'de',
    regionName: 'Germany',
    regionType: 'Country'
  },
  nordics: {
    regionId: 'se',
    regionName: 'Sweden',
    regionType: 'Country'
  },
  iberia: {
    regionId: 'es',
    regionName: 'Spain',
    regionType: 'Country'
  },
  'uk-ireland': {
    regionId: 'uk',
    regionName: 'United Kingdom',
    regionType: 'Country'
  },
  italy: {
    regionId: 'it',
    regionName: 'Italy',
    regionType: 'Country'
  },
  'eastern-europe': {
    regionId: 'pl',
    regionName: 'Poland',
    regionType: 'Country'
  },
  'all-europe': {
    regionId: 'de',
    regionName: 'Germany',
    regionType: 'Country'
  }
};
const noop = () => {};
export function TemplateView() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    createFromTemplate
  } = useWorkspaces();
  const {
    profile
  } = useUserProfile();
  const template = MANAGED_TEMPLATES.find(t => t.id === id);
  // Derive location override from user profile defaults
  const locationOverride = useMemo<LocationOverride | null>(() => {
    return REGION_OVERRIDES[profile.defaultRegion] || null;
  }, [profile.defaultRegion]);
  const handleOpenAsWorkspace = () => {
    if (!template) return;
    const newId = createFromTemplate(template.id);
    if (newId) {
      navigate(`/workspace/${newId}`);
    }
  };
  if (!template) {
    return <div className="jua-page-bg h-[calc(100vh-64px)] w-full flex items-center justify-center" data-id="element-4212">
        <div className="text-center" data-id="element-4213">
          <p className="text-slate-400 text-sm" data-id="element-4214">Template not found</p>
          <button onClick={() => navigate('/workspaces')} className="mt-3 text-[#EC4899] text-sm hover:underline" data-id="element-4215">
            Go to Workspaces
          </button>
        </div>
      </div>;
  }
  const widgets: Widget[] = template.widgets;
  return <div className="jua-page-bg h-[calc(100vh-64px)] w-full flex flex-col overflow-hidden" data-id="element-4216">
      {/* Toolbar */}
      <div className="app-toolbar border-b border-slate-800 bg-[#0F172A]/65 backdrop-blur-[48px] backdrop-saturate-[180%] px-3 md:px-6 py-2 flex items-center gap-x-3 flex-shrink-0" data-id="element-4217">
        {/* Left: template info */}
        <div className="flex items-center gap-2.5 min-w-0" data-id="element-4218">
          <Lock size={16} className="text-slate-500 flex-shrink-0" data-id="element-4219" />
          <div className="min-w-0" data-id="element-4220">
            <h1 className="text-sm sm:text-base md:text-lg font-semibold text-white flex items-center gap-2" data-id="element-4221">
              <span className="truncate" data-id="element-4222">{template.name}</span>
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-slate-400 flex-shrink-0" data-id="element-4223">
                Template
              </span>
            </h1>
            <p className="text-[11px] text-slate-500 truncate hidden sm:block" data-id="element-4224">
              {template.description}
            </p>
          </div>
        </div>

        {/* Center: read-only notice */}
        <div className="hidden md:flex items-center gap-1.5 text-[11px] text-slate-500 ml-4" data-id="element-4225">
          <Info size={12} data-id="element-4226" />
          <span data-id="element-4227">Read-only preview · Using your default settings</span>
        </div>

        {/* Right: Open as Workspace */}
        <div className="flex items-center gap-2 ml-auto flex-shrink-0" data-id="element-4228">
          <Button size="sm" className="h-8 gap-2" onClick={handleOpenAsWorkspace} data-id="element-4229">
            <ExternalLink size={14} data-id="element-4230" />
            <span className="hidden sm:inline" data-id="element-4231">Open as Workspace</span>
            <span className="sm:hidden" data-id="element-4232">Open</span>
          </Button>
        </div>
      </div>

      {/* Grid Canvas — static, non-editable */}
      <div className="flex-1 overflow-y-auto p-2 md:p-6" data-id="element-4233">
        <ResponsiveGridLayout className="layout" layouts={{
        xl: widgets.map(w => ({
          i: w.i,
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h
        })),
        lg: widgets.map(w => ({
          i: w.i,
          x: w.x,
          y: w.y,
          w: Math.min(w.w, 12),
          h: w.h
        })),
        md: widgets.map(w => ({
          i: w.i,
          x: w.x,
          y: w.y,
          w: Math.min(w.w, 12),
          h: w.h
        })),
        sm: widgets.map((w, i) => ({
          i: w.i,
          w: 12,
          x: 0,
          y: i * 4,
          h: w.h
        })),
        xs: widgets.map((w, i) => ({
          i: w.i,
          w: 12,
          x: 0,
          y: i * 4,
          h: w.h
        })),
        xxs: widgets.map((w, i) => ({
          i: w.i,
          w: 12,
          x: 0,
          y: i * 4,
          h: w.h
        }))
      }} breakpoints={{
        xl: 1600,
        lg: 1200,
        md: 900,
        sm: 600,
        xs: 480,
        xxs: 0
      }} cols={{
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
        xxs: 12
      }} rowHeight={100} isDraggable={false} isResizable={false} margin={[24, 24]} compactType="vertical" data-id="element-4234">
          {widgets.map(widget => <div key={widget.i} data-id="element-4235">
              {widget.type === 'chart' ? <ChartWidget id={`tpl-${template.id}-${widget.i}`} onRemove={noop} onDuplicate={noop} onParameterChange={noop} onDoubleClickHandle={noop} modelOverride={null} locationOverride={locationOverride} readOnly data-id="element-4236" /> : widget.type === 'map' ? <MapWidget id={`tpl-${template.id}-${widget.i}`} onRemove={noop} onDuplicate={noop} onToggleBackground={noop} isBackground={false} onParameterChange={noop} onDoubleClickHandle={noop} modelOverride={null} locationOverride={locationOverride} initialOverlay={widget.config?.overlay} initialPOIs={widget.config?.pois} readOnly data-id="element-4237" /> : widget.type === 'text' ? <TextWidget id={`tpl-${template.id}-${widget.i}`} onRemove={noop} onDuplicate={noop} onParameterChange={noop} onDoubleClickHandle={noop} initialPrompt={widget.config?.prompt} readOnly data-id="element-4238" /> : widget.type === 'table' ? <TableWidget id={`tpl-${template.id}-${widget.i}`} onRemove={noop} onDuplicate={noop} onParameterChange={noop} onDoubleClickHandle={noop} modelOverride={null} locationOverride={locationOverride} readOnly data-id="element-4239" /> : null}
            </div>)}
        </ResponsiveGridLayout>
      </div>
    </div>;
}